import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { trackLessonStarted } from '../../utils/analytics';
import { List, X } from '@phosphor-icons/react';
import Layout from '../../components/layout/Layout';
import ChatMessage from '../../components/lessons/ChatMessage';
import ChatInput, { ChatInputRef } from '../../components/lessons/ChatInput';
import LessonDetailsSidebar from '../../components/lessons/LessonDetailsSidebar';
import PDFUploader from '../../components/lessons/PDFUploader';
import LessonContextPanel from '../../components/lessons/LessonContextPanel';
import VoiceTextDisplay, { VoiceTextDisplayRef } from '../../components/voice/VoiceTextDisplay';
import { Message, LessonDetails, StartLessonResponse, TutorInteractionResponse } from '../../types/lesson';
import { useAuth } from '../../contexts/AuthContext';
import { lessonAPI } from '../../utils/api';
import { getSoundEffects } from '../../utils/soundEffects';
import WalkthroughTour from '../../components/walkthrough/WalkthroughTour';
import { lessonGeneralSteps, lessonVoiceSteps, lessonPdfContextSteps } from '../../components/walkthrough/tourSteps';
import type { LessonMode } from '../../components/walkthrough/ModeSelectionModal';
import { VOICE_MODE_ENABLED } from '../../config/features';
import { useViewport } from '../../hooks/useViewport';
import ContactSupportModal from '../../components/support/ContactSupportModal';
import { shouldShowTour } from '../../config/tourConfig';

export default function LessonPage() {
  const { user, userProfile, refreshProfile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [lessonDetails, setLessonDetails] = useState<LessonDetails | null>(null);
  const [currentLessonId, setCurrentLessonId] = useState<string | null>(null);
  const [lessonMode, setLessonMode] = useState<LessonMode>('text'); // 'voice' or 'text'
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isEndingLesson, setIsEndingLesson] = useState(false);
  const [isVoiceConnected, setIsVoiceConnected] = useState(false);
  const [aiThinkingMessage, setAiThinkingMessage] = useState<string | null>(null);
  const [isAiSpeaking, setIsAiSpeaking] = useState(false);
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [isUserRecording, setIsUserRecording] = useState(false);
  const [isSoundEnabled, setIsSoundEnabled] = useState(true);
  const [isProcessingImage, setIsProcessingImage] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef<Message[]>([]);
  const voiceDisplayRef = useRef<VoiceTextDisplayRef>(null);
  const voiceClientRef = useRef<any>(null); // Reference to VoiceTutorClient for image uploads
  const soundEffectsRef = useRef(getSoundEffects());
  const effectRunCountRef = useRef(0); // Track how many times voice init effect runs
  const chatInputRef = useRef<ChatInputRef>(null);

  // Tour state - manage tour sections
  const [showGeneralTour, setShowGeneralTour] = useState(false);
  const [showVoiceTour, setShowVoiceTour] = useState(false);
  const [showPdfTour, setShowPdfTour] = useState(false);
  const [lessonStarted, setLessonStarted] = useState(false);

  // PDF Context state
  const [showPDFUploadModal, setShowPDFUploadModal] = useState(false);
  const [hasLessonContext, setHasLessonContext] = useState(false);
  const [contextPanelKey, setContextPanelKey] = useState(0); // For forcing refresh

  // Mobile drawer state
  const { isMobile } = useViewport();
  const [isRightPanelOpen, setIsRightPanelOpen] = useState(false);
  const [isContactSupportOpen, setIsContactSupportOpen] = useState(false);
  const [shouldBounceHandle, setShouldBounceHandle] = useState(false);

  // Track when lesson has actually started
  useEffect(() => {
    // For text mode: lesson starts as soon as we have lesson details
    // For voice mode: lesson starts when voice connects
    if (lessonMode === 'text' && lessonDetails) {
      trackLessonStarted('text', lessonDetails.subject.name);
      setLessonStarted(true);
    } else if (lessonMode === 'voice' && isVoiceConnected) {
      trackLessonStarted('voice', lessonDetails?.subject.name);
      setLessonStarted(true);
    }
  }, [lessonMode, lessonDetails, isVoiceConnected]);

  // Show lesson general tour only AFTER lesson has started
  useEffect(() => {
    if (lessonStarted && userProfile && shouldShowTour(userProfile.tour_lesson_general, userProfile.tour_enabled, 'lesson-general')) {
      setShowGeneralTour(true);
    }
  }, [lessonStarted, userProfile]);

  // Show voice tour when voice call connects (only in voice mode, after lesson started)
  useEffect(() => {
    if (lessonMode === 'voice' && isVoiceConnected && lessonStarted && userProfile && shouldShowTour(userProfile.tour_lesson_voice, userProfile.tour_enabled)) {
      // Wait for general tour to finish first
      if (!showGeneralTour && (userProfile.tour_lesson_general || shouldShowTour(false))) {
        setShowVoiceTour(true);
      }
    }
  }, [lessonMode, isVoiceConnected, lessonStarted, userProfile, showGeneralTour]);

  // Show PDF context tour (NEW - shows to ALL users, even if tours were disabled)
  useEffect(() => {
    if (lessonStarted && userProfile && shouldShowTour(userProfile.tour_lesson_pdf_context, true, 'lesson-pdf-context')) {
      // Wait for other tours to finish first
      if (!showGeneralTour && !showVoiceTour) {
        // Small delay to ensure the lesson context panel is rendered
        const timer = setTimeout(() => {
          setShowPdfTour(true);
        }, 500);
        return () => clearTimeout(timer);
      }
    }
  }, [lessonStarted, userProfile, showGeneralTour, showVoiceTour]);

  // Bounce the handle when lesson starts (only if user has seen the PDF tour)
  useEffect(() => {
    if (isMobile && lessonStarted && userProfile?.tour_lesson_pdf_context) {
      // User has seen the tour, so bounce the handle as a reminder
      const timer = setTimeout(() => {
        setShouldBounceHandle(true);
        // Stop bouncing after 3 seconds
        setTimeout(() => {
          setShouldBounceHandle(false);
        }, 3000);
      }, 1000); // Small delay after lesson starts
      return () => clearTimeout(timer);
    }
  }, [isMobile, lessonStarted, userProfile?.tour_lesson_pdf_context]);

  // Stable callback references for tour handlers
  const handleGeneralTourComplete = useCallback(() => {
    setShowGeneralTour(false);
    refreshProfile();
  }, [refreshProfile]);

  const handleGeneralTourSkip = useCallback(() => {
    setShowGeneralTour(false);
    refreshProfile();
  }, [refreshProfile]);

  const handleVoiceTourComplete = useCallback(() => {
    setShowVoiceTour(false);
    refreshProfile();
  }, [refreshProfile]);

  const handleVoiceTourSkip = useCallback(() => {
    setShowVoiceTour(false);
    refreshProfile();
  }, [refreshProfile]);

  const handlePdfTourComplete = useCallback(() => {
    setShowPdfTour(false);
    refreshProfile();
  }, [refreshProfile]);

  const handlePdfTourSkip = useCallback(() => {
    setShowPdfTour(false);
    refreshProfile();
  }, [refreshProfile]);

  // Handle tour events for opening/closing panels
  useEffect(() => {
    const handleOpenRightPanel = () => {
      if (isMobile) {
        setIsRightPanelOpen(true);
      }
    };

    const handleCloseRightPanel = () => {
      if (isMobile) {
        setIsRightPanelOpen(false);
      }
    };

    const handleBounceHandle = () => {
      if (isMobile) {
        setShouldBounceHandle(true);
        // Stop bouncing after 3 seconds
        setTimeout(() => {
          setShouldBounceHandle(false);
        }, 3000);
      }
    };

    window.addEventListener('tour:open-right-panel', handleOpenRightPanel);
    window.addEventListener('tour:close-right-panel', handleCloseRightPanel);
    window.addEventListener('tour:bounce-handle', handleBounceHandle);

    return () => {
      window.removeEventListener('tour:open-right-panel', handleOpenRightPanel);
      window.removeEventListener('tour:close-right-panel', handleCloseRightPanel);
      window.removeEventListener('tour:bounce-handle', handleBounceHandle);
    };
  }, [isMobile]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Keep ref in sync with messages for cleanup
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Handle typing sounds when AI is thinking (only in voice mode)
  useEffect(() => {
    const soundEffects = soundEffectsRef.current;

    console.log('Typing sound effect check:', {
      isTyping,
      aiThinkingMessage: !!aiThinkingMessage,
      aiThinkingMessageText: aiThinkingMessage,
      isAiSpeaking,
      isSoundEnabled,
      isProcessingImage,
      lessonMode,
      shouldPlay: lessonMode === 'voice' && (isTyping || aiThinkingMessage) && !isAiSpeaking && isSoundEnabled
    });

    if (lessonMode === 'voice' && (isTyping || aiThinkingMessage) && !isAiSpeaking && isSoundEnabled) {
      // Start typing sounds when AI is thinking and not speaking and sound is enabled (voice mode only)
      console.log('Starting typing sounds');
      soundEffects.startTypingSounds();
    } else {
      // Stop typing sounds when AI is not thinking or when AI starts speaking or sound is disabled
      console.log('Stopping typing sounds');
      soundEffects.stopTypingSounds();
    }

    // Cleanup function
    return () => {
      soundEffects.stopTypingSounds();
    };
  }, [isTyping, aiThinkingMessage, isAiSpeaking, isSoundEnabled, isProcessingImage, lessonMode]);

  useEffect(() => {
    // Get lesson data from navigation state
    const lessonData = location.state?.lessonData as StartLessonResponse;
    const mode = VOICE_MODE_ENABLED ? ((location.state?.lessonMode as LessonMode) || 'text') : 'text';

    if (!lessonData) {
      // No lesson data, redirect back to dashboard
      navigate('/dashboard');
      return;
    }

    // Set lesson mode
    setLessonMode(mode);

    // Set up lesson details
    setCurrentLessonId(lessonData.lesson_id);
    setLessonDetails({
      id: lessonData.lesson_id,
      subject: {
        name: lessonData.subject_name,
        topic: lessonData.topic,
      },
      resources: [], // You might want to add resources based on the course
    });

    // Add initial message from tutor
    // Handle both string and structured response formats
    let initialContent = 'Welcome to your lesson!';
    let initialDisplayItems: any[] | undefined;

    if (lessonData.initial_message) {
      if (typeof lessonData.initial_message === 'string') {
        initialContent = lessonData.initial_message;
      } else if (typeof lessonData.initial_message === 'object') {
        // Structured response format
        const structuredMessage = lessonData.initial_message as any;
        initialContent = structuredMessage.text_content || structuredMessage.spoken_content || 'Welcome to your lesson!';
        initialDisplayItems = structuredMessage.display_content;
      }
    }

    const initialMessage: Message = {
      id: '1',
      content: initialContent,
      sender: {
        id: 'tutor',
        name: 'AI Tutor',
        role: 'tutor',
      },
      timestamp: new Date(),
      displayItems: initialDisplayItems,
    };

    setMessages([initialMessage]);
  }, [location.state, navigate]);

  // Handle ending lesson when component unmounts or user navigates away
  useEffect(() => {
    const handleEndLesson = async () => {
      if (currentLessonId) {
        try {
          // First, stop the voice session if it's active
          if (voiceClientRef.current) {
            console.log('Cleanup: Ending voice session...');
            await voiceClientRef.current.endVoiceCall();
            setIsVoiceConnected(false);
          }
          
          // Then end the lesson
          await lessonAPI.endLesson(currentLessonId);
        } catch (error) {
          console.error('Failed to end lesson:', error);
        }
      }
    };

    const handleBeforeUnload = () => {
      if (currentLessonId) {
        // For page refresh/close, we use navigator.sendBeacon for reliability
        const token = localStorage.getItem('access_token');
        const url = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/lessons/${currentLessonId}/end`;
        
        // Immediately stop voice session and audio playback synchronously
        if (voiceClientRef.current) {
          try {
            // Call endVoiceCall to properly cleanup the voice session
            // This is synchronous cleanup that will stop audio immediately
            console.log('Synchronous voice cleanup on page unload...');
            voiceClientRef.current.endVoiceCall();
          } catch (error) {
            console.error('Error during voice cleanup on page unload:', error);
          }
        }
        
        // Create a blob with the request data including headers
        const data = new Blob([JSON.stringify({})], {
          type: 'application/json'
        });
        
        // Note: sendBeacon doesn't support custom headers, so this is a fallback
        // The server should handle graceful lesson cleanup even without this
        navigator.sendBeacon(url, data);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      
      // Immediately stop voice session and audio playback when component unmounts
      if (voiceClientRef.current) {
        console.log('Component unmount: Immediately stopping voice session...');
        try {
          // Synchronously stop all audio immediately
          voiceClientRef.current.endVoiceCall();
        } catch (error) {
          console.error('Error during voice cleanup on component unmount:', error);
        }
        voiceClientRef.current = null;
        setIsVoiceConnected(false);
      }
      
      // Then handle lesson ending asynchronously
      handleEndLesson();
      
      // Cleanup sound effects
      if (soundEffectsRef.current) {
        soundEffectsRef.current.dispose();
      }
      
      // Cleanup object URLs to prevent memory leaks
      messagesRef.current.forEach(message => {
        if (message.image?.preview) {
          URL.revokeObjectURL(message.image.preview);
        }
      });
    };
  }, [currentLessonId]);

  // Initialize voice client directly - ONLY in voice mode
  useEffect(() => {
    effectRunCountRef.current += 1;
    console.log('🔵 [LESSON-DEBUG] Voice init useEffect triggered (run #' + effectRunCountRef.current + ')');
    console.log('🔵 [LESSON-DEBUG] Effect state:', {
      currentLessonId,
      lessonMode,
      hasVoiceClient: !!voiceClientRef.current,
      voiceClientRefValue: voiceClientRef.current
    });

    let checkConnectionInterval: NodeJS.Timeout | null = null;

    if (currentLessonId && !voiceClientRef.current && lessonMode === 'voice') {
      console.log('🔵 [LESSON-DEBUG] Conditions met - importing VoiceTutorClient...');
      // Import and initialize VoiceTutorClient dynamically
      import('../../components/voice/VoiceTutorClient').then(({ VoiceTutorClient }) => {
        console.log('🔵 [LESSON-DEBUG] VoiceTutorClient imported successfully');
        console.log('🔵 [LESSON-DEBUG] voiceClientRef.current before assignment:', voiceClientRef.current);

        const client = new VoiceTutorClient();
        console.log('🔵 [LESSON-DEBUG] VoiceTutorClient instance created');
        voiceClientRef.current = client;

        console.log('🔵 [LESSON-DEBUG] Client assigned to ref, starting voice call...');
        
        // Set up callbacks
        client.setCallbacks({
          onConnectionStatusChange: (status: string, text: string) => {
            console.log('Voice connection status:', status, text);
            setIsVoiceConnected(status === 'connected' || status === 'teaching');
          },
          onModeChange: (isConnected: boolean) => {
            setIsVoiceConnected(isConnected);
          },
          onShowErrorModal: (title: string, message: string, actionText?: string, onAction?: () => void) => {
            console.error('Voice error:', title, message);
          },
          onCanStartRecording: () => {
            // Prevent recording when processing an image
            return !isProcessingImage;
          },
          onUserRecordingStateChange: (isRecording: boolean) => {
            console.log('User recording state changed:', isRecording);
            setIsUserRecording(isRecording);
            setIsUserSpeaking(isRecording); // Update the visual indicator
            
            if (isRecording) {
              // Clear typing indicator when starting new recording
              console.log('onUserRecordingStateChange: Started recording');
              setIsTyping(false);
              setAiThinkingMessage(null);
            } else {
              // Show typing indicator when user stops recording (response is being processed)
              console.log('onUserRecordingStateChange: Stopped recording');
              setIsTyping(true);
            }
          },
          onAiSpeakingStateChange: (isSpeaking: boolean) => {
            console.log('AI speaking state changed:', isSpeaking);
            console.log('Current processing states when AI speaking changes:', {
              isProcessingImage,
              isTyping,
              aiThinkingMessage: !!aiThinkingMessage
            });
            setIsAiSpeaking(isSpeaking);
            
            // Clear thinking message when AI starts speaking (streaming audio)
            if (isSpeaking) {
              console.log('onAiSpeakingStateChange: AI started speaking, clearing thinking states');
              setAiThinkingMessage(null);
              setIsTyping(false);
            }
          },
          onTutorResponse: (response: any) => {
            console.log('Voice tutor response received:', response);
            console.log('Current processing states when tutor responds:', {
              isProcessingImage,
              isTyping,
              aiThinkingMessage: !!aiThinkingMessage
            });
            
            // Clear any thinking message and typing indicator
            console.log('onTutorResponse: Clearing thinking states');
            setAiThinkingMessage(null);
            setIsTyping(false);
            
            // Handle the new display_content structure
            if (response.display_content && response.display_content.length > 0) {
              // Create a single message with all display items
              const displayMessage: Message = {
                id: `voice-display-${Date.now()}`,
                content: '', // No text content for display-only messages
                sender: {
                  id: 'tutor',
                  name: 'AI Tutor',
                  role: 'tutor',
                },
                timestamp: new Date(),
                messageType: 'voice_display_content',
                displayItems: response.display_content // Use the new DisplayItem[] structure
              };

              setMessages(prev => {
                // Check if this exact message already exists to avoid duplicates
                const exists = prev.some(msg => 
                  msg.messageType === 'voice_display_content' &&
                  msg.sender.role === 'tutor' &&
                  Math.abs(new Date(msg.timestamp).getTime() - Date.now()) < 5000
                );
                if (!exists) {
                  return [...prev, displayMessage];
                }
                return prev;
              });
            }
          }
        });

        // Get DOM elements for the client (create dummy elements since we're not using the old UI)
        const elements = {
          startCallBtn: document.createElement('button'),
          endCallBtn: document.createElement('button'),
          muteBtn: document.createElement('button'),
          volumeSlider: document.createElement('input'),
          textDisplay: voiceDisplayRef.current?.getTextDisplayElement() || document.createElement('div'),
          tutorAudio: document.createElement('audio'),
          connectionStatus: document.createElement('div'),
          mathExpressionsList: voiceDisplayRef.current?.getMathExpressionsElement() || document.createElement('div'),
          typingIndicator: voiceDisplayRef.current?.getTypingIndicatorElement() || document.createElement('div')
        };

        client.initializeElements(elements);
        voiceClientRef.current = client;
        
        // Set up a periodic check for connection state changes
        checkConnectionInterval = setInterval(() => {
          if (voiceClientRef.current) {
            const connected = voiceClientRef.current.getIsConnected();
            setIsVoiceConnected(connected);
          }
        }, 1000); // Check every second

        // Auto-start the voice session
        console.log('🔵 [LESSON-DEBUG] Calling client.startVoiceCall with lessonId:', currentLessonId);
        client.startVoiceCall(currentLessonId);
      }).catch(error => {
        console.error('🔵 [LESSON-DEBUG] Failed to initialize voice client:', error);
      });
    } else {
      console.log('🔵 [LESSON-DEBUG] Skipping voice init:', {
        reason: !currentLessonId ? 'no lessonId' :
                voiceClientRef.current ? 'client already exists' :
                lessonMode !== 'voice' ? 'not voice mode' : 'unknown'
      });
    }

    // Cleanup function
    return () => {
      console.log('🔵 [LESSON-DEBUG] Voice init useEffect cleanup (run #' + effectRunCountRef.current + ')');
      if (checkConnectionInterval) {
        clearInterval(checkConnectionInterval);
      }
    };
  }, [currentLessonId, lessonMode]);

  const handleSendMessage = async (content: string, image?: File) => {
    if (!user || !currentLessonId || isLoading) return;

    let imagePreview: string | undefined;
    if (image) {
      imagePreview = URL.createObjectURL(image);
      console.log('Image detected, setting isProcessingImage to true');
      setIsProcessingImage(true); // Disable voice recording when processing image
      console.log('Current state when image processing starts:', {
        isProcessingImage: true, // This will be the new state
        isTyping,
        aiThinkingMessage: !!aiThinkingMessage,
        isAiSpeaking,
        isSoundEnabled
      });
    }

    // Add user message to chat
    const userMessage: Message = {
      id: Date.now().toString(),
      content: typeof content === 'string' ? content : '',
      sender: {
        id: user.id,
        name: user.fullName,
        role: 'student',
        avatar: user.profileImage,
      },
      timestamp: new Date(),
      image: image && imagePreview ? {
        file: image,
        preview: imagePreview
      } : undefined,
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    console.log('User message sent:', userMessage);
    
    // Set typing indicator and thinking message
    setIsTyping(true);
    console.log('Set isTyping to true, processing:', image ? 'image' : 'text');
    console.log('Current state before thinking message:', {
      isTyping: true,
      isAiSpeaking,
      isSoundEnabled,
      isProcessingImage
    });

    // Show AI thinking messages
    const thinkingMessages = image ? [
      "Analyzing your image...",
      "Let me examine this image carefully...",
      "Processing the visual content...",
      "Reading the text and analyzing the image...",
      "Extracting information from your image..."
    ] : [
      "Let me think about that...",
      "Hmm, that's a good point...",
      "I'm processing your question...", 
      "Give me a moment to formulate my response...",
      "Interesting question, let me analyze this..."
    ];
    
    const randomThinking = thinkingMessages[Math.floor(Math.random() * thinkingMessages.length)];
    setAiThinkingMessage(randomThinking);
    console.log('Set AI thinking message:', randomThinking);
    console.log('State after setting thinking message:', {
      isTyping: true,
      aiThinkingMessage: randomThinking,
      isAiSpeaking,
      isSoundEnabled,
      isProcessingImage
    });

    // If there's an active voice session and an image, also show it in the voice display
    if (image && voiceClientRef.current?.getIsConnected() && voiceDisplayRef.current) {
      voiceDisplayRef.current.addUploadedImage(image, content || "Can you help me with this image?");
    }

    try {
      const response: TutorInteractionResponse = await lessonAPI.interact(currentLessonId, {
        message: content || undefined,
        image: image || undefined,
      });
      
      // Create tutor message with display content if available
      // Handle both nested response object and direct string response
      const responseText = typeof response.response === 'string'
        ? response.response
        : (response.response.text_content || 'Error: Invalid response received');

      // Extract display content from response
      const displayContent = typeof response.response === 'object'
        ? response.response.display_content
        : response.display_content;

      const tutorMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: responseText,
        sender: {
          id: 'tutor',
          name: 'AI Tutor',
          role: 'tutor',
        },
        timestamp: new Date(),
        // Only add display content if it has items
        ...(displayContent && displayContent.length > 0 ? { displayItems: displayContent } : {})
      };

      setMessages(prev => [...prev, tutorMessage]);
      console.log('Clearing AI thinking message after successful response');
      setAiThinkingMessage(null); // Clear thinking message

      // Log OCR results if image was processed
      if (response.image_processed && response.extracted_text) {
        console.log('OCR extracted text:', response.extracted_text);
        console.log('OCR success:', response.ocr_success);
      }

      // Log if voice response was sent
      if (response.voice_response_sent) {
        console.log('Voice response was automatically sent through active voice session');
      }
    } catch (error: any) {
      console.error('Failed to send message:', error);
      
      // Add error message to chat
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: 'Sorry, I encountered an error. Please try again.',
        sender: {
          id: 'tutor',
          name: 'AI Tutor',
          role: 'tutor',
        },
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, errorMessage]);
      console.log('Clearing AI thinking message after error');
      setAiThinkingMessage(null); // Clear thinking message
    } finally {
      setIsLoading(false);
      console.log('In finally block - clearing typing and image processing states');
      console.log('Final state changes:', {
        setIsTyping: false,
        setIsProcessingImage: false
      });
      setIsTyping(false);
      setIsProcessingImage(false); // Re-enable voice recording after image processing is complete

      // Auto-focus the input field after AI responds
      setTimeout(() => {
        chatInputRef.current?.focus();
      }, 100);
    }
  };

  const handleStopRecording = async () => {
    if (voiceClientRef.current && isUserRecording) {
      try {
        await voiceClientRef.current.stopManualRecording();
      } catch (error) {
        console.error('Failed to stop recording:', error);
      }
    }
  };

  const toggleSound = () => {
    const newSoundState = !isSoundEnabled;
    setIsSoundEnabled(newSoundState);
    soundEffectsRef.current.setSoundEnabled(newSoundState);
    
    if (!newSoundState) {
      soundEffectsRef.current.stopTypingSounds();
    }
  };

  const handleEndLesson = async () => {
    if (!currentLessonId || isEndingLesson) return;

    const shouldEnd = window.confirm('Are you sure you want to end this lesson?');
    if (!shouldEnd) return;

    setIsEndingLesson(true);
    try {
      // First, immediately stop the voice session and audio if it's active
      if (voiceClientRef.current) {
        console.log('Ending voice session and stopping audio...');
        await voiceClientRef.current.endVoiceCall();
        voiceClientRef.current = null;
        setIsVoiceConnected(false);
      }

      // Then end the lesson
      await lessonAPI.endLesson(currentLessonId);
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Failed to end lesson:', error);
      navigate('/dashboard');
    } finally {
      setIsEndingLesson(false);
    }
  };

  const handlePDFUpload = async (files: File[], mode: 'reset' | 'append') => {
    if (!currentLessonId) return;

    try {
      const result = await lessonAPI.uploadMaterials(currentLessonId, files, mode);

      // Add a system message showing the context confirmation
      const contextMessage: Message = {
        id: Date.now().toString(),
        content: result.text_content || result.lesson_context || 'Lesson context updated successfully',
        sender: {
          id: 'system',
          name: 'System',
          role: 'tutor'
        },
        timestamp: new Date()
      };

      setMessages(prev => [...prev, contextMessage]);
      setHasLessonContext(true);
      setContextPanelKey(prev => prev + 1); // Force context panel refresh
      setShowPDFUploadModal(false);
    } catch (error: any) {
      console.error('Failed to upload PDFs:', error);
      alert(error.message || 'Failed to upload PDF files');
    }
  };

  const handleContextUpdate = () => {
    // Refresh context panel
    setContextPanelKey(prev => prev + 1);
  };

  if (!lessonDetails) {
    return (
      <Layout>
        <div className="flex flex-1 justify-center items-center">
          <div className="text-white text-lg">Loading lesson...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout onContactSupport={() => setIsContactSupportOpen(true)}>
      <div className="gap-1 px-2 md:px-6 flex flex-1 justify-center py-2 md:py-5">
        <div className="layout-content-container flex flex-col w-full md:max-w-[920px] flex-1">
          {/* Mobile header */}
          <div className="flex justify-between items-center px-2 md:px-4 pb-3 pt-3 md:pt-5">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <div className="min-w-0 flex-1">
                <h2 className="text-white tracking-light text-lg md:text-[28px] font-bold leading-tight truncate">
                  {lessonDetails.subject.name} Lesson
                </h2>
                <p className="text-white text-xs md:text-base font-normal leading-normal pt-1 flex items-center gap-2 truncate">
                  <span className="truncate">Topic: {lessonDetails.subject.topic}</span>
                  <span className="text-white/60 hidden md:inline">•</span>
                  <span className="text-white/80 text-xs md:text-sm hidden md:inline">
                    {lessonMode === 'voice' ? 'Voice Mode' : 'Text Mode'}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Speaking Indicators - like Google Meets/Skype */}
          <div className="px-2 md:px-4 py-3 border-b border-gray-600/30">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
                {/* User Speaking Indicator */}
                <div className="flex items-center gap-1 md:gap-2">
                  <div className="relative">
                    <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center ${
                      user?.profileImage ? 'bg-gray-600' : 'bg-primary'
                    }`}>
                      {user?.profileImage ? (
                        <img src={user.profileImage} alt={user.fullName} className="w-full h-full rounded-full object-cover" />
                      ) : (
                        <span className="text-white text-xs md:text-sm font-semibold">
                          {user?.fullName?.charAt(0) || 'U'}
                        </span>
                      )}
                    </div>
                    {/* Speaking indicator rings - shown when user is speaking */}
                    {(isVoiceConnected && isUserSpeaking) && (
                      <>
                        <div className="absolute inset-0 rounded-full border-2 border-green-400 animate-ping opacity-30"></div>
                        <div className="absolute inset-0 rounded-full border-2 border-green-400 animate-pulse"></div>
                      </>
                    )}
                  </div>
                  <span className="text-white text-xs md:text-sm hidden sm:inline">{user?.fullName || 'You'}</span>
                </div>

                {/* AI Speaking Indicator */}
                <div className="flex items-center gap-1 md:gap-2">
                  <div className="relative">
                    <div className="w-8 h-8 md:w-10 md:h-10 bg-gray-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs md:text-sm font-semibold">AI</span>
                    </div>
                    {/* Speaking indicator rings - shown when AI is speaking/thinking */}
                    {(isTyping || isLoading || aiThinkingMessage || isAiSpeaking) && (
                      <>
                        <div className="absolute inset-0 rounded-full border-2 border-blue-400 animate-ping opacity-30"></div>
                        <div className="absolute inset-0 rounded-full border-2 border-blue-400 animate-pulse"></div>
                      </>
                    )}
                  </div>
                  <span className="text-white text-xs md:text-sm hidden sm:inline">AI Tutor</span>
                </div>
              </div>

              {/* Voice Status and Actions */}
              <div className="flex items-center gap-1 md:gap-4 flex-wrap">
                {/* Sound Toggle - Desktop only */}
                {lessonMode === 'voice' && !isMobile && (
                  <button
                    onClick={toggleSound}
                    className="flex items-center gap-2 text-gray-400 hover:text-white text-sm transition-colors"
                    title={isSoundEnabled ? "Disable typing sounds" : "Enable typing sounds"}
                  >
                    {isSoundEnabled ? (
                      <>
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.764L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.797-3.764a1 1 0 011 .076zM7 9.5a.5.5 0 01.5-.5h1a.5.5 0 01.5.5v1a.5.5 0 01-.5.5h-1a.5.5 0 01-.5-.5v-1z" clipRule="evenodd" />
                          <path d="M11.025 7.05a.5.5 0 01.707 0c.146.146.366.229.586.229s.44-.083.586-.229a.5.5 0 11.707.707c-.293.293-.677.457-1.086.457s-.793-.164-1.086-.457a.5.5 0 010-.707z" />
                        </svg>
                        Sound On
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.764L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.797-3.764a1 1 0 011 .076zM12.293 7.293a1 1 0 011.414 0L15 8.586l1.293-1.293a1 1 0 111.414 1.414L16.414 10l1.293 1.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                        Sound Off
                      </>
                    )}
                  </button>
                )}

                {/* Voice Connection Status - only show in voice mode on desktop */}
                {lessonMode === 'voice' && !isMobile && (
                  <div className="flex items-center gap-2">
                    {isVoiceConnected ? (
                      <div className="flex items-center gap-2 text-green-400 text-sm">
                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                        Voice Active
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-gray-400 text-sm">
                        <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                        Voice Connecting...
                      </div>
                    )}
                  </div>
                )}

                {/* Upload Context Materials Button - Desktop only, mobile has in drawer */}
                {!isMobile && (
                  <button
                    onClick={() => setShowPDFUploadModal(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center gap-2 text-sm"
                    title="Upload PDF materials for lesson context"
                    data-tour="pdf-upload-button"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <span className="hidden lg:inline">Upload Context</span>
                    <span className="lg:hidden">Upload</span>
                  </button>
                )}

                {/* End Lesson Button */}
                <button
                  onClick={handleEndLesson}
                  disabled={isEndingLesson}
                  className="px-2 md:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 md:gap-2 text-xs md:text-sm"
                >
                  {isEndingLesson ? (
                    <>
                      <div className="w-3 h-3 md:w-4 md:h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span className="hidden md:inline">Ending...</span>
                    </>
                  ) : (
                    <>
                      <span className="hidden md:inline">End Lesson</span>
                      <span className="md:hidden">End</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Combined Chat + Voice Interface */}
          <div className="flex-1 overflow-y-auto px-2 md:px-4">
            {/* Chat Messages (including voice activity) */}
            {messages.map((message) => (
              <ChatMessage
                key={message.id}
                message={message}
                isCurrentUser={user?.id === message.sender.id}
              />
            ))}
            
            {/* AI Thinking Message */}
            {(isTyping || aiThinkingMessage) && (
              <div className="flex items-start gap-4 px-4 py-3">
                <div className="flex-shrink-0 w-10 h-10 bg-gray-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-semibold">AI</span>
                </div>
                <div className="flex-1">
                  <div className="text-white text-sm font-medium mb-1">AI Tutor</div>
                  <div className="text-white/80 italic">
                    <span className="inline-flex items-center gap-2">
                      {aiThinkingMessage || "Thinking..."}
                      <span className="flex gap-1">
                        <span className="w-1 h-1 bg-blue-400 rounded-full animate-bounce"></span>
                        <span className="w-1 h-1 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></span>
                        <span className="w-1 h-1 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></span>
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Hidden Voice Text Display for voice functionality */}
            <div style={{ display: 'none' }}>
              <VoiceTextDisplay
                ref={voiceDisplayRef}
                isVoiceMode={true}
                className="text-sm"
              />
            </div>
            
            <div ref={messagesEndRef} />
          </div>

          <ChatInput
            ref={chatInputRef}
            onSendMessage={handleSendMessage}
            disabled={isLoading}
            isVoiceActive={isVoiceConnected}
            isUserRecording={isUserRecording}
            onStopRecording={handleStopRecording}
            isProcessingImage={isProcessingImage}
          />
        </div>

        {/* Desktop Sidebar - hidden on mobile */}
        {!isMobile && (
          <div className="w-80 flex-shrink-0 space-y-4">
            <LessonDetailsSidebar details={lessonDetails} />
            <LessonContextPanel
              key={contextPanelKey}
              lessonId={currentLessonId!}
              onContextUpdate={handleContextUpdate}
            />
          </div>
        )}
      </div>

      {/* Mobile Right Panel with Handle */}
      {isMobile && (
        <>
          {/* Backdrop */}
          <div
            className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity duration-300 ${
              isRightPanelOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            onClick={() => setIsRightPanelOpen(false)}
          />
          
          {/* Panel Handle - visible when panel is closed */}
          {!isRightPanelOpen && (
            <button
              onClick={() => {
                setIsRightPanelOpen(true);
                setShouldBounceHandle(false);
              }}
              className={`fixed top-1/2 right-0 -translate-y-1/2 z-40 bg-blackboard border-l border-t border-b border-border rounded-l-lg py-6 px-2 shadow-lg transition-all duration-300 ${
                shouldBounceHandle ? 'animate-bounce' : ''
              }`}
              aria-label="Open lesson info"
              data-tour="right-panel-handle"
            >
              <div className="flex flex-col items-center gap-1">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </div>
            </button>
          )}
          
          {/* Right Panel */}
          <div
            className={`fixed top-0 right-0 h-full w-[85%] max-w-[400px] bg-blackboard border-l border-border z-50 transition-transform duration-300 ease-in-out overflow-y-auto ${
              isRightPanelOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            <div className="p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-white text-lg font-bold">Lesson Info</h3>
                <button
                  onClick={() => setIsRightPanelOpen(false)}
                  className="text-chalk-white hover:text-chalk-blue transition-colors"
                >
                  <X size={24} />
                </button>
              </div>
              
              {/* Upload Context Button */}
              <button
                onClick={() => {
                  setIsRightPanelOpen(false);
                  setShowPDFUploadModal(true);
                }}
                className="w-full mb-6 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2 text-sm"
                data-tour="pdf-upload-button"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                Upload Context Materials
              </button>
              
              {/* Lesson Details Section */}
              <div className="mb-6">
                <h4 className="text-white text-base font-semibold mb-3 pb-2 border-b border-border">Lesson Details</h4>
                <LessonDetailsSidebar details={lessonDetails} />
              </div>
              
              {/* Lesson Context Section */}
              <div>
                <h4 className="text-white text-base font-semibold mb-3 pb-2 border-b border-border">Lesson Context</h4>
                <LessonContextPanel
                  key={contextPanelKey}
                  lessonId={currentLessonId!}
                  onContextUpdate={handleContextUpdate}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {/* PDF Upload Modal */}
      {showPDFUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Upload Lesson Context Materials</h2>
              <button
                onClick={() => setShowPDFUploadModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Close"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              Upload worksheets, assignments, or other PDF documents to establish context for this lesson.
              The AI tutor will use this context to provide more relevant help.
            </p>
            <PDFUploader
              onUpload={handlePDFUpload}
              hasExistingContext={hasLessonContext}
            />
          </div>
        </div>
      )}

      {/* Contextual Tour Sections */}
      {showGeneralTour && (
        <WalkthroughTour
          section="lesson-general"
          steps={lessonGeneralSteps}
          isActive={true}
          onComplete={handleGeneralTourComplete}
          onSkip={handleGeneralTourSkip}
        />
      )}

      {showVoiceTour && (
        <WalkthroughTour
          section="lesson-voice"
          steps={lessonVoiceSteps}
          isActive={true}
          onComplete={handleVoiceTourComplete}
          onSkip={handleVoiceTourSkip}
        />
      )}

      {showPdfTour && (
        <WalkthroughTour
          section="lesson-pdf-context"
          steps={lessonPdfContextSteps}
          isActive={true}
          onComplete={handlePdfTourComplete}
          onSkip={handlePdfTourSkip}
        />
      )}

      {/* Contact Support Modal */}
      <ContactSupportModal
        isOpen={isContactSupportOpen}
        onClose={() => setIsContactSupportOpen(false)}
      />
    </Layout>
  );
}
